import { useState } from 'react';
import type { CellarDTO } from '@adega/shared';
import { ApiError } from '../api/client.js';
import { useAddShelf, useUpdateShelf } from '../api/queries.js';
import { ErrorBox } from './Feedback.js';

interface GarrafaAfetada {
  id: number;
  name: string;
  position: number;
}

/**
 * Editor de prateleiras com a confirmação de redução de capacidade.
 *
 * Reduzir capacidade nunca apaga garrafa: o backend recusa e devolve quem
 * ficaria de fora; aqui mostramos a lista e só então reenviamos com `force`.
 */
export function ShelfEditor({ cellar }: { cellar: CellarDTO }) {
  const atualizar = useUpdateShelf();
  const adicionar = useAddShelf(cellar.id);

  const [emEdicao, setEmEdicao] = useState<number | null>(null);
  const [rascunho, setRascunho] = useState<{ label: string; capacity: number } | null>(null);
  const [confirmacao, setConfirmacao] = useState<{
    shelfId: number;
    capacity: number;
    label: string;
    garrafas: GarrafaAfetada[];
  } | null>(null);
  const [novaCapacidade, setNovaCapacidade] = useState(8);

  const salvar = async (shelfId: number, força = false) => {
    if (!rascunho && !força) return;

    const dados = força && confirmacao
      ? { label: confirmacao.label, capacity: confirmacao.capacity }
      : rascunho!;

    try {
      await atualizar.mutateAsync({ id: shelfId, ...dados, force: força });
      setEmEdicao(null);
      setRascunho(null);
      setConfirmacao(null);
    } catch (erro) {
      if (erro instanceof ApiError && erro.code === 'CAPACITY_WOULD_UNPLACE_BOTTLES') {
        const detalhes = erro.details as { bottlesToUnplace?: GarrafaAfetada[] } | undefined;
        setConfirmacao({
          shelfId,
          capacity: dados.capacity,
          label: dados.label,
          garrafas: detalhes?.bottlesToUnplace ?? [],
        });
        return;
      }
      throw erro;
    }
  };

  return (
    <section className="card space-y-3">
      <h2 className="font-semibold">Prateleiras</h2>

      <div className="space-y-2">
        {cellar.shelves.map((shelf) => {
          const editando = emEdicao === shelf.id;

          return (
            <div key={shelf.id} className="rounded-xl border border-adega-border p-3">
              {editando ? (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      className="field flex-1"
                      aria-label="Nome da prateleira"
                      value={rascunho?.label ?? shelf.label}
                      onChange={(e) =>
                        setRascunho({
                          label: e.target.value,
                          capacity: rascunho?.capacity ?? shelf.capacity,
                        })
                      }
                    />
                    <input
                      type="number"
                      min={1}
                      max={200}
                      aria-label="Capacidade"
                      className="field w-20"
                      value={rascunho?.capacity ?? shelf.capacity}
                      onChange={(e) =>
                        setRascunho({
                          label: rascunho?.label ?? shelf.label,
                          capacity: Number(e.target.value),
                        })
                      }
                    />
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="btn-primary flex-1"
                      disabled={atualizar.isPending}
                      onClick={() => void salvar(shelf.id)}
                    >
                      Salvar
                    </button>
                    <button
                      type="button"
                      className="btn-secondary"
                      onClick={() => {
                        setEmEdicao(null);
                        setRascunho(null);
                        setConfirmacao(null);
                      }}
                    >
                      Cancelar
                    </button>
                  </div>

                  {/* A resposta do servidor fica colada no botao que a pediu.
                      Antes ela era renderizada depois da lista inteira de
                      prateleiras: com sete delas, a confirmacao aparecia fora
                      da tela e o Salvar parecia nao fazer nada. */}
                  {confirmacao?.shelfId === shelf.id ? (
                    <div className="rounded-xl border border-amber-500/40 bg-amber-500/10 p-3 text-sm">
                      <p className="font-semibold text-amber-200">
                        {confirmacao.garrafas.length === 1
                          ? '1 garrafa ficaria sem lugar'
                          : `${confirmacao.garrafas.length} garrafas ficariam sem lugar`}
                      </p>
                      <ul className="mt-1 list-inside list-disc text-adega-muted">
                        {confirmacao.garrafas.map((g) => (
                          <li key={g.id}>
                            {g.name} (posição {g.position + 1})
                          </li>
                        ))}
                      </ul>
                      <p className="mt-2 text-adega-muted">
                        Não serão apagadas: vão para a lista “sem posição” para você
                        realocar.
                      </p>
                      <div className="mt-3 flex gap-2">
                        <button
                          type="button"
                          className="btn-primary flex-1"
                          disabled={atualizar.isPending}
                          onClick={() => void salvar(confirmacao.shelfId, true)}
                        >
                          Confirmar redução
                        </button>
                        <button
                          type="button"
                          className="btn-secondary"
                          onClick={() => setConfirmacao(null)}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : null}

                  {atualizar.error && !confirmacao ? <ErrorBox erro={atualizar.error} /> : null}
                </div>
              ) : (
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="font-medium">{shelf.label}</p>
                    <p className="text-xs text-adega-muted">
                      {shelf.occupied}/{shelf.capacity} ocupadas
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={() => {
                      setEmEdicao(shelf.id);
                      setRascunho({ label: shelf.label, capacity: shelf.capacity });
                      // Confirmacao pendente e de outra prateleira: comecar a
                      // editar esta zera a conversa anterior.
                      setConfirmacao(null);
                    }}
                  >
                    Editar
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-end gap-2 border-t border-adega-border pt-3">
        <div className="flex-1">
          <label className="label" htmlFor="nova-prateleira">
            Adicionar prateleira
          </label>
          <input
            id="nova-prateleira"
            type="number"
            min={1}
            max={200}
            className="field"
            value={novaCapacidade}
            onChange={(e) => setNovaCapacidade(Number(e.target.value))}
          />
        </div>
        <button
          type="button"
          className="btn-secondary"
          disabled={adicionar.isPending}
          onClick={() => void adicionar.mutateAsync({ capacity: novaCapacidade })}
        >
          Adicionar
        </button>
      </div>

      {adicionar.error ? <ErrorBox erro={adicionar.error} /> : null}
    </section>
  );
}
